// Passport certificate list admin routes. Mirrors the API's passport-cert-list
// admin controller by hand — the server is authoritative for shapes.

import { apiGet, apiPost } from './client';

export type CandidateStatus =
  | 'pending'
  | 'approved'
  | 'rolled_back'
  | 'rejected'
  | 'superseded'
  | 'blocked'
  | 'already_live';

export interface ReportCert {
  country: string;
  countryName: string;
  subjectCN: string | null;
  validTo: string;
  fingerprint256: string;
}

export interface CertListReport {
  before: { certificates: number; countries: number };
  after: { certificates: number; countries: number };
  added: ReportCert[];
  removed: ReportCert[];
  newCountries: string[];
  removedCountries: string[];
  expired: ReportCert[];
}

export interface CheckOutcome {
  hardStops: { code: string; message: string }[];
  warnings: { code: string; message: string }[];
  passed: string[];
}

export interface PullRequestResult {
  base: string;
  outcome: 'opened' | 'already_current' | 'already_open' | 'failed';
  url?: string;
  error?: string;
}

export interface CandidateView {
  id: string;
  version: string;
  listDate: string;
  status: CandidateStatus;
  url: string | null;
  sha256: string | null;
  sizeBytes: number | null;
  certificateCount: number;
  countryCount: number;
  report: CertListReport | null;
  checks: CheckOutcome;
  pullRequests: PullRequestResult[] | null;
  createdAt: string;
  decidedAt: string | null;
  decidedByEmail: string | null;
  isLive: boolean;
}

export interface PassportCertListLive {
  passportCertListVersion: string | null;
  passportCertListURL: string | null;
  passportCertListSHA256: string | null;
  passportCertListSizeBytes: number | null;
  passportCertListPublishedAt: string | null;
  passportCertListReleaseNotes: string | null;
}

export interface PassportCertListOverview {
  live: PassportCertListLive | null;
  watch: {
    lastCheckedAt: string | null;
    consecutiveFailures: number;
    lastError: string | null;
  };
  candidates: CandidateView[];
}

export type WatchOutcome =
  | 'not_modified'
  | 'unchanged'
  | 'already_live'
  | 'staged'
  | 'blocked'
  | 'failed';

export interface WatchResult {
  outcome: WatchOutcome;
  candidateId?: string;
  version?: string;
  message: string;
}

export interface ProbeSide {
  ok: boolean;
  detail: string;
}

export interface ProbeResult {
  spaces: ProbeSide;
  github: ProbeSide;
}

const BASE = '/admin/passport-cert-list';

export const getPassportCertListOverview = (): Promise<PassportCertListOverview> =>
  apiGet<PassportCertListOverview>(BASE);

export const checkPassportCertListNow = (): Promise<WatchResult> =>
  apiPost<WatchResult>(`${BASE}/check`);

export const probePassportCertList = (): Promise<ProbeResult> =>
  apiPost<ProbeResult>(`${BASE}/probe`);

const act = (id: string, verb: 'approve' | 'reject' | 'rollback'): Promise<CandidateView> =>
  apiPost<CandidateView>(`${BASE}/${encodeURIComponent(id)}/${verb}`);

export const approvePassportCertList = (id: string) => act(id, 'approve');
export const rejectPassportCertList = (id: string) => act(id, 'reject');
export const rollbackPassportCertList = (id: string) => act(id, 'rollback');
